import { computed, type Ref, type ComputedRef } from 'vue';
import type { TeamMemberEntity } from '@/api/entity_types/TeamMember';

export type UserSelectorOption = {
  label: string;
  value: string;
};

export type UserSelectorOptions = {
  selectedType: Ref<string> | ComputedRef<string>;
  selectedUserIds: Ref<number[]> | ComputedRef<number[]>;
  teamMembers: Ref<TeamMemberEntity[]> | ComputedRef<TeamMemberEntity[]>;
  typeOptions: UserSelectorOption[];
  showUsersForType: string;
  onTypeChange: (val: string) => void;
  onUserIdsChange: (val: number[]) => void;
};

export function useUserSelector(options: UserSelectorOptions) {
  const { selectedType, selectedUserIds, teamMembers, typeOptions, showUsersForType, onTypeChange, onUserIdsChange } =
    options;

  const typeDisplay = computed(() => {
    const option = typeOptions.find(opt => opt.value === selectedType.value);
    return option?.label ?? '—';
  });

  const selectedUserNames = computed(() => {
    if (selectedUserIds.value.length === 0) return [];
    const selectedMembers = teamMembers.value.filter(m => selectedUserIds.value.includes(m.id));
    return selectedMembers.map(m => m.name?.trim() || m.email?.trim() || '').filter(name => name.length > 0);
  });

  const sortedTeamMembers = computed(() => {
    return [...teamMembers.value].sort((a, b) => {
      const aSelected = selectedUserIds.value.includes(a.id);
      const bSelected = selectedUserIds.value.includes(b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
  });

  const showUserList = computed(() => selectedType.value === showUsersForType);

  const handleTypeChange = (val?: string) => {
    if (!val) return;
    onTypeChange(val);
    if (val !== showUsersForType) {
      onUserIdsChange([]);
    }
  };

  const toggleUser = (userId: number) => {
    const currentIds = [...selectedUserIds.value];
    const index = currentIds.indexOf(userId);
    if (index > -1) {
      currentIds.splice(index, 1);
    } else {
      currentIds.push(userId);
    }
    onUserIdsChange(currentIds);
  };

  return {
    typeDisplay,
    selectedUserNames,
    sortedTeamMembers,
    showUserList,
    handleTypeChange,
    toggleUser,
  };
}
